import type { ScreenerColumn } from "epsm-api-types/models";
import { scorerPrefix } from "../../../default";
import type { ScorerColumn } from "../../../type";
import { fncClassNameScorerTotal } from "../../const";
import { getColumnName } from "../../Scorer";

export const scorerTotal = {
	name: `${scorerPrefix}total`,
	title: "Total Score",
	groupName: "All",
	type: "num",
	fncClassName: fncClassNameScorerTotal,
} as ScreenerColumn;

export const addScoringColumns = <T extends ScreenerColumn | string>(
	scorerColumns: ScorerColumn[],
	prevColumns: T[],
): T[] => {
	const getName = (col: ScreenerColumn | string) => {
		return typeof col === "string" ? col : col.name;
	};
	const filterRemovedScorerColumns = (
		prevColumns: T[],
		columnsToCompare: T[] | ScorerColumn[],
	) => {
		return prevColumns.filter((col) =>
			getName(col).startsWith(scorerPrefix)
				? getName(col) === scorerTotal.name ||
					columnsToCompare.some(
						(scorerCol) => getName(scorerCol) === getName(col),
					)
				: true,
		);
	};

	let preTotalElements: T[] = [];
	const newScoringElements: T[] = [];

	const filteredPrevColumns = filterRemovedScorerColumns(
		prevColumns,
		scorerColumns,
	);

	if (!filteredPrevColumns.find((col) => getName(col) === scorerTotal.name)) {
		const tickerAndName = ["ticker", "name"];

		for (const name of tickerAndName) {
			const col = filteredPrevColumns.find((c) => getName(c) === name);
			if (col) {
				preTotalElements.push(col);
			}
		}

		// then add scorer_total
		if (typeof filteredPrevColumns[0] === "string") {
			preTotalElements.push(scorerTotal.name as T);
		} else {
			preTotalElements.push(scorerTotal as T);
		}
	} else {
		preTotalElements = filteredPrevColumns.slice(
			0,
			filteredPrevColumns.findIndex(
				(col) => getName(col) === scorerTotal.name,
			) + 1,
		);
	}

	const newScorerColumns = scorerColumns.filter(
		(col) => !filteredPrevColumns.some((c) => getName(c) === getName(col)),
	);

	// add [prev column, scorerColumn] pairs for new scorer columns
	for (const scorerCol of newScorerColumns) {
		const baseName = getColumnName(scorerCol);
		if (baseName === "total") continue;

		const baseCol = filteredPrevColumns.find((c) => getName(c) === baseName);

		if (baseCol) {
			newScoringElements.push(baseCol);
		}

		if (typeof filteredPrevColumns[0] === "string") {
			newScoringElements.push(scorerCol.name as T);
		} else {
			newScoringElements.push(scorerCol as T);
		}
	}

	const filteredPreTotalElements = preTotalElements.filter(
		(col) =>
			!newScoringElements.some((sccol) => getName(sccol) === getName(col)),
	);

	const setElements = [...filteredPreTotalElements, ...newScoringElements];
	const restElements = filteredPrevColumns.filter(
		(col) => !setElements.some((setCol) => getName(setCol) === getName(col)),
	);
	const result = [...setElements, ...restElements];

	return result;
};
